import { createFileRoute, Link } from "@tanstack/react-router";
import { buildAlbumParam } from "@/lib/slugify";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Camera,
  X,
  Zap,
  ZapOff,
  Loader2,
  Search,
  Disc3,
  Heart,
  Share2,
  ExternalLink,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  Music2,
  Layers,
  Gem,
  DollarSign,
} from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { EyebrowBadge } from "@/components/EyebrowBadge";
import {
  lookupByCoverImage,
  lookupByQuery,
  getGarimpoDetail,
  type GarimpoResult,
} from "@/lib/garimpo.functions";
import {
  searchByCoverEmbedding,
  upsertAlbumEmbedding,
  type AlbumMatch,
} from "@/lib/embeddings.functions";
import { embedImage } from "@/lib/clip-client";
import { addAlbum, listMyAlbums } from "@/lib/collection.functions";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ALBUM_COVER_FALLBACK, handleCoverError } from "@/lib/album-cover-fallback";

export const Route = createFileRoute("/garimpo")({
  head: () => ({
    meta: [
      { title: "Modo Garimpo — Escaneie discos | Viny Club" },
      {
        name: "description",
        content:
          "Aponte a câmera para a capa ou código de barras e descubra detalhes da prensagem, preço médio e raridade do disco em segundos.",
      },
      { property: "og:title", content: "Modo Garimpo — Viny Club" },
      {
        property: "og:description",
        content: "Escaneie discos e descubra detalhes instantaneamente.",
      },
    ],
  }),
  component: GarimpoPage,
});

type Phase = "idle" | "scanning" | "searching" | "result" | "visual" | "manual";

function GarimpoPage() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [result, setResult] = useState<GarimpoResult | null>(null);
  const [lastBarcode, setLastBarcode] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [visualMatches, setVisualMatches] = useState<AlbumMatch[]>([]);
  const upsertEmbedding = useServerFn(upsertAlbumEmbedding);
  const { isAuthenticated } = useCurrentUser();

  // Seed the visual index in the background: embed the cover of any album
  // surfaced by the Gemini fallback so future visual searches find it.
  const seedCoverEmbedding = useCallback(
    async (album: { discogsId: string; cover: string; title: string; artist: string; year: number | null; genres?: string[] }) => {
      if (!isAuthenticated || !album.cover || !album.discogsId) return;
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = album.cover;
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error("cover load failed"));
        });
        const embedding = await embedImage(img);
        await upsertEmbedding({
          data: {
            discogsId: album.discogsId,
            embedding,
            title: album.title,
            artist: album.artist,
            year: album.year ?? null,
            genre: album.genres?.[0] ?? null,
            cover: album.cover,
          },
        });
      } catch (e) {
        // silent — seeding is best-effort
        console.warn("seedCoverEmbedding failed", e);
      }
    },
    [isAuthenticated, upsertEmbedding],
  );



  return (
    <AppShell>
      {/* hero */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        {/* background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-background to-background" />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
            backgroundSize: "22px 22px",
          }}
        />
        {/* floating vinyl glow */}
        <motion.div
          aria-hidden
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full border border-primary/20 bg-gradient-to-br from-primary/20 to-transparent blur-2xl lg:-right-10"
        />
        <motion.div
          aria-hidden
          initial={{ rotate: 0 }}
          animate={{ rotate: -360 }}
          transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          className="pointer-events-none absolute -bottom-32 -left-20 h-[360px] w-[360px] rounded-full border border-primary/10 blur-2xl"
        />

        <div className="relative mx-auto max-w-5xl px-4 py-14 lg:px-8 lg:py-24">
          <EyebrowBadge>Beta · Caça ao tesouro</EyebrowBadge>
          <h1 className="mt-3 font-display text-4xl font-bold leading-[0.95] tracking-tight lg:text-6xl">
            Modo <em className="font-serif italic">Garimpo</em>
          </h1>
          <p className="mt-4 max-w-xl text-base text-muted-foreground lg:text-lg">
            Aponte a câmera, escaneie o disco e descubra tudo o que importa
            antes de levar para casa.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={() => setPhase("scanning")}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-lg transition-transform hover:scale-105"
            >
              <Camera className="h-4 w-4" /> Abrir scanner
            </button>
            <button
              onClick={() => setPhase("manual")}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:bg-surface/80"
            >
              <Search className="h-4 w-4" /> Buscar manualmente
            </button>
          </div>

          {/* value props */}
          <div className="mt-12">
            <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              O que você descobre
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Feature
                icon={Music2}
                title="Prévias das músicas"
                text="Escute trechos do álbum antes de decidir."
              />
              <Feature
                icon={Layers}
                title="Edição e prensagem"
                text="País, ano e detalhes da prensagem específica."
              />
              <Feature
                icon={Gem}
                title="Raridade"
                text="Quão difícil é encontrar essa edição no mercado."
              />
              <Feature
                icon={DollarSign}
                title="Preço médio"
                text="Valor histórico negociado para essa prensagem."
              />
            </div>
          </div>
        </div>
      </section>


      <AnimatePresence mode="wait">
        {phase === "scanning" && (
          <ScannerOverlay
            key="scanner"
            onClose={() => setPhase("idle")}
            onCaptured={(imageDataUrl) => {
              setCapturedImage(imageDataUrl);
              setLastBarcode(null);
              setPhase("searching");
            }}
          />
        )}
        {phase === "searching" && capturedImage && (
          <SearchingFlow
            key="searching"
            imageDataUrl={capturedImage}
            onVisualMatches={(matches) => {
              setVisualMatches(matches);
              setCapturedImage(null);
              setPhase("visual");
            }}
            onDone={(r, barcode) => {
              setLastBarcode(barcode ?? null);
              setCapturedImage(null);
              if (r) {
                setResult(r);
                setPhase("result");
                // grow the visual index for next time
                void seedCoverEmbedding({
                  discogsId: r.discogsId,
                  cover: r.cover,
                  title: r.title,
                  artist: r.artist,
                  year: r.year ?? null,
                  genres: r.genres,
                });
              } else {
                toast.error("Disco não encontrado. Tente buscar pelo nome.");
                setPhase("manual");
              }
            }}
            onCancel={() => {
              setCapturedImage(null);
              setPhase("idle");
            }}
          />
        )}
        {phase === "visual" && (
          <VisualResultsModal
            key="visual"
            matches={visualMatches}
            onClose={() => {
              setVisualMatches([]);
              setPhase("idle");
            }}
            onScanAgain={() => {
              setVisualMatches([]);
              setPhase("scanning");
            }}
            onOpenDetail={async (m) => {
              try {
                const detail = await fetchDetail(m.discogsId);
                setResult(detail);
                setPhase("result");
              } catch (e: any) {
                toast.error(e?.message || "Falha ao buscar disco");
              }
            }}
          />
        )}
        {phase === "result" && result && (
          <ResultModal
            key="result"
            result={result}
            barcode={lastBarcode}
            onClose={() => {
              setResult(null);
              setCapturedImage(null);
              setPhase("idle");
            }}
            onScanAgain={() => {
              setResult(null);
              setCapturedImage(null);
              setPhase("scanning");
            }}
          />
        )}
        {phase === "manual" && (
          <ManualSearchModal
            key="manual"
            onClose={() => setPhase("idle")}
            onPick={async (releaseId) => {
              setCapturedImage(null);
              // reuse searching by reusing detail; we set a synthetic flow:
              try {
                // fetch detail directly
                const detail = await fetchDetail(releaseId);
                setResult(detail);
                setPhase("result");
              } catch (e: any) {
                toast.error(e?.message || "Falha ao buscar disco");
                setPhase("manual");
              }
            }}
          />
        )}
      </AnimatePresence>
    </AppShell>
  );
}

// --- helper: detail call from client ---
async function fetchDetail(releaseId: string): Promise<GarimpoResult> {
  const fn = (await import("@/lib/garimpo.functions")).getGarimpoDetail;
  return fn({ data: { releaseId } });
}

function Feature({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof Camera;
  title: string;
  text: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-border bg-surface/60 p-5 backdrop-blur transition-colors hover:border-primary/40 hover:bg-surface">
      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-primary/10 blur-2xl transition-opacity group-hover:opacity-100 opacity-60" />
      <div className="relative grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/20">
        <Icon className="h-5 w-5" />
      </div>
      <div className="relative mt-3 font-display text-base font-semibold tracking-tight">
        {title}
      </div>
      <p className="relative mt-1 text-sm leading-relaxed text-muted-foreground">
        {text}
      </p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Scanner overlay (fullscreen camera + ZXing)
// ───────────────────────────────────────────────────────────────────────────

function ScannerOverlay({
  onClose,
  onCaptured,
}: {
  onClose: () => void;
  onCaptured: (imageDataUrl: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [hint, setHint] = useState("Enquadre a capa ou o código de barras");
  const [isCapturing, setIsCapturing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let hintTimer: ReturnType<typeof setInterval> | null = null;
    (async () => {
      try {
        const constraints: MediaStreamConstraints = {

          audio: false,
          video: {
            facingMode: { ideal: "environment" },
          },
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }

        // Reset zoom to minimum if supported (prevents mobile auto-zoom crop)
        try {
          const track = stream?.getVideoTracks?.()[0];
          if (track) {
            const caps = (track.getCapabilities?.() ?? {}) as any;
            if (caps?.zoom && typeof caps.zoom.min === "number") {
              await track.applyConstraints({ advanced: [{ zoom: caps.zoom.min } as any] });
            } else if (caps?.zoom) {
              await track.applyConstraints({ advanced: [{ zoom: 1 } as any] });
            }
          }
        } catch {}

        const phrases = [
          "Enquadre a capa inteira sem cortar",
          "Também leio código de barras",
          "Segure firme e toque no botão",
          "Quanto mais luz, melhor a busca",
        ];
        let i = 0;
        hintTimer = setInterval(() => {
          i = (i + 1) % phrases.length;
          setHint(phrases[i]);
        }, 2200);
      } catch (e: any) {
        setError(
          e?.name === "NotAllowedError"
            ? "Permissão de câmera negada. Ative nas configurações do navegador."
            : e?.message || "Não foi possível abrir a câmera.",
        );
      }
    })();
    return () => {
      cancelled = true;
      if (hintTimer) clearInterval(hintTimer);
      try {
        streamRef.current?.getTracks().forEach((track) => track.stop());
      } catch {}
      try {
        const d = document as any;
        if (d.exitFullscreen) d.exitFullscreen();
        else if (d.webkitExitFullscreen) d.webkitExitFullscreen();
      } catch {}
    };
  }, []);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight || isCapturing) return;
    setIsCapturing(true);
    const maxSide = 1400;
    const scale = Math.min(1, maxSide / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setIsCapturing(false);
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    onCaptured(canvas.toDataURL("image/jpeg", 0.86));
  }, [isCapturing, onCaptured]);

  const toggleTorch = async () => {
    const stream = streamRef.current;
    const track = stream?.getVideoTracks?.()[0];
    if (!track) return;
    const caps = (track.getCapabilities?.() ?? {}) as any;
    if (!caps.torch) {
      toast.error("Flash não disponível nesse dispositivo");
      return;
    }
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn } as any] });
      setTorchOn((v) => !v);
    } catch {}
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black overflow-hidden"
      style={{
        height: "100dvh",
        width: "100vw",
        top: 0,
        left: 0,
      }}
    >
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="absolute inset-0 h-full w-full object-cover"
      />
      
      {/* scrim */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/80" />

      {/* top bar */}
      <div
        className="absolute inset-x-0 top-0 flex items-center justify-between px-4 pt-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 12px)" }}
      >
        <button
          onClick={onClose}
          className="grid h-10 w-10 place-items-center rounded-full bg-black/50 text-white backdrop-blur"
          aria-label="Fechar scanner"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="rounded-full bg-black/50 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
          Modo garimpo
        </div>
        <button
          onClick={toggleTorch}
          className="grid h-10 w-10 place-items-center rounded-full bg-black/50 text-white backdrop-blur"
          aria-label="Lanterna"
        >
          {torchOn ? <ZapOff className="h-5 w-5" /> : <Zap className="h-5 w-5" />}
        </button>
      </div>

      {/* scan frame */}
      <div className="pointer-events-none absolute inset-0 grid place-items-center">
        <div className="relative h-[92vw] w-[92vw] max-h-[72vh] max-w-[72vh] rounded-3xl sm:h-[70vw] sm:w-[70vw] sm:max-h-[520px] sm:max-w-[520px] lg:h-[520px] lg:w-[520px]">
          {/* corners */}
          {[
            "top-0 left-0 border-l-2 border-t-2 rounded-tl-3xl",
            "top-0 right-0 border-r-2 border-t-2 rounded-tr-3xl",
            "bottom-0 left-0 border-l-2 border-b-2 rounded-bl-3xl",
            "bottom-0 right-0 border-r-2 border-b-2 rounded-br-3xl",
          ].map((cls) => (
            <span
              key={cls}
              className={`absolute h-10 w-10 border-primary ${cls}`}
            />
          ))}
          {/* scanning line */}
          <motion.div
            initial={{ y: "0%" }}
            animate={{ y: "100%" }}
            transition={{
              duration: 2.2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
            className="absolute inset-x-4 top-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_18px_2px_hsl(var(--primary))]"
          />
        </div>
      </div>

      {/* bottom controls */}
      <div
        className="absolute inset-x-0 bottom-0 z-10 flex flex-col items-center gap-4 px-6 pb-6 pt-10"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 20px)" }}
      >
        <AnimatePresence mode="wait">
          <motion.p
            key={hint}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="rounded-full bg-black/70 px-4 py-2 text-center text-sm font-medium text-white/90 backdrop-blur"
          >
            {hint}
          </motion.p>
        </AnimatePresence>

        <button
          type="button"
          onClick={capturePhoto}
          disabled={isCapturing || !!error}
          className="group flex flex-col items-center gap-2 disabled:opacity-50"
          aria-label="Tirar foto e identificar disco"
        >
          <span className="flex h-20 w-20 items-center justify-center rounded-full border-[6px] border-white/90 bg-white text-black shadow-2xl ring-4 ring-black/30 transition-transform active:scale-90">
            {isCapturing ? (
              <Loader2 className="h-7 w-7 animate-spin" />
            ) : (
              <Camera className="h-8 w-8" />
            )}
          </span>
          <span className="rounded-full bg-black/70 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur">
            {isCapturing ? "Enviando..." : "Tirar foto"}
          </span>
        </button>
      </div>

      {error && (
        <div className="absolute inset-x-6 top-1/2 z-20 -translate-y-1/2 rounded-2xl border border-destructive/40 bg-background/95 p-5 text-center shadow-2xl">
          <AlertTriangle className="mx-auto h-6 w-6 text-destructive" />
          <p className="mt-2 text-sm text-foreground">{error}</p>
          <button
            onClick={onClose}
            className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            Fechar
          </button>
        </div>
      )}
    </motion.div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Searching state — full-screen loader while we hit Discogs
// ───────────────────────────────────────────────────────────────────────────

function SearchingFlow({
  imageDataUrl,
  onDone,
  onVisualMatches,
  onCancel,
}: {
  imageDataUrl: string;
  onDone: (r: GarimpoResult | null, barcode?: string | null) => void;
  onVisualMatches: (matches: AlbumMatch[]) => void;
  onCancel: () => void;
}) {
  const lookup = useServerFn(lookupByCoverImage);
  const searchByEmbedding = useServerFn(searchByCoverEmbedding);
  const [phrase, setPhrase] = useState("Carregando IA no seu navegador…");
  const [modelProgress, setModelProgress] = useState<number | null>(null);

  useEffect(() => {
    const phrases = [
      "Gerando assinatura visual…",
      "Comparando com o catálogo…",
      "Procurando capas parecidas…",
      "Quase lá",
    ];
    let i = 0;
    const t = setInterval(() => {
      i = (i + 1) % phrases.length;
      setPhrase(phrases[i]);
    }, 1300);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Step 1 — embed locally via CLIP (model cached after first load)
        const embedding = await embedImage(imageDataUrl, (info: any) => {
          if (info?.status === "progress" && typeof info.progress === "number") {
            setModelProgress(Math.round(info.progress));
          } else if (info?.status === "ready") {
            setModelProgress(null);
          }
        });
        if (cancelled) return;
        setPhrase("Procurando capas parecidas…");

        // Step 2 — query pgvector for similar covers
        const { matches } = await searchByEmbedding({
          data: { embedding, matchCount: 8 },
        });
        if (cancelled) return;

        const strong = matches.filter((m) => m.similarity >= 0.55);
        if (strong.length > 0) {
          onVisualMatches(strong);
          return;
        }

        // Step 3 — fallback: identify via Gemini Vision + text search
        setPhrase("Sem match visual — identificando pela capa…");
        const res = await lookup({ data: { imageDataUrl } });
        if (cancelled) return;
        onDone(
          res.found ? res.result : null,
          res.found ? res.barcode : res.identification?.barcode,
        );
      } catch (e: any) {
        if (cancelled) return;
        console.error(e);
        toast.error(e?.message || "Falha ao analisar a imagem");
        onDone(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [imageDataUrl, lookup, searchByEmbedding, onDone, onVisualMatches]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] grid place-items-center bg-background/95 backdrop-blur"
    >
      <div className="text-center px-6">
        <div className="relative mx-auto h-24 w-24">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 rounded-full border-2 border-dashed border-primary/40"
          />
          <div className="absolute inset-3 grid place-items-center rounded-full bg-surface">
            <Disc3 className="h-9 w-9 animate-pulse text-primary" />
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.p
            key={phrase}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="mt-6 font-display text-lg font-semibold"
          >
            {phrase}
          </motion.p>
        </AnimatePresence>
        {modelProgress != null ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Baixando modelo CLIP… {modelProgress}% (só na primeira vez)
          </p>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">
            Análise 100% local — nada da foto sai do seu aparelho
          </p>
        )}
        <button
          onClick={onCancel}
          className="mt-6 text-xs font-semibold text-muted-foreground underline-offset-4 hover:underline"
        >
          Cancelar
        </button>
      </div>
    </motion.div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Visual results — grid of CLIP matches with quick actions
// ───────────────────────────────────────────────────────────────────────────

function VisualResultsModal({
  matches,
  onClose,
  onScanAgain,
  onOpenDetail,
}: {
  matches: AlbumMatch[];
  onClose: () => void;
  onScanAgain: () => void;
  onOpenDetail: (m: AlbumMatch) => void;
}) {
  const { isAuthenticated } = useCurrentUser();
  const qc = useQueryClient();
  const add = useServerFn(addAlbum);
  const [pending, setPending] = useState<string | null>(null);

  const doAdd = async (m: AlbumMatch, status: "owned" | "wishlist") => {
    if (!isAuthenticated) {
      toast.error("Faça login para salvar discos");
      return;
    }
    setPending(`${m.discogsId}:${status}`);
    try {
      await add({
        data: {
          discogsId: m.discogsId,
          status,
          listing: "none",
          title: m.title,
          artist: m.artist,
          year: m.year,
          cover: m.cover,
          genres: m.genre ? [m.genre] : [],
        },
      });
      toast.success(
        status === "owned" ? `Adicionado: ${m.title}` : `Salvo na wishlist: ${m.title}`,
      );
      qc.invalidateQueries({ queryKey: ["my-collection"] });
      qc.invalidateQueries({ queryKey: ["my-wishlist"] });
      qc.invalidateQueries({ queryKey: ["my-albums"] });
    } catch (e: any) {
      toast.error(e?.message || "Falha ao salvar");
    } finally {
      setPending(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] overflow-y-auto bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        onClick={(e) => e.stopPropagation()}
        className="mx-auto my-0 min-h-screen w-full max-w-3xl bg-background sm:my-8 sm:min-h-0 sm:rounded-3xl sm:border sm:border-border sm:shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/95 px-4 py-3 backdrop-blur sm:rounded-t-3xl">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
              Busca visual
            </div>
            <div className="font-display text-lg font-bold">
              {matches.length} {matches.length === 1 ? "disco parecido" : "discos parecidos"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onScanAgain}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-surface/70"
            >
              <Camera className="h-3.5 w-3.5" /> Outra foto
            </button>
            <button
              onClick={onClose}
              className="grid h-9 w-9 place-items-center rounded-full bg-surface text-foreground hover:bg-surface/70"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid gap-3 px-4 py-5 sm:grid-cols-2">
          {matches.map((m) => {
            const sim = Math.round(m.similarity * 100);
            const addingOwned = pending === `${m.discogsId}:owned`;
            const addingWish = pending === `${m.discogsId}:wishlist`;
            return (
              <div
                key={m.discogsId}
                className="flex flex-col gap-3 rounded-2xl border border-border bg-surface/60 p-3 transition-colors hover:border-primary/40"
              >
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => onOpenDetail(m)}
                    className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-surface"
                  >
                    {m.cover ? (
                      <img
                        src={m.cover}
                        alt={m.title}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="grid h-full w-full place-items-center text-muted-foreground">
                        <Disc3 className="h-6 w-6" />
                      </div>
                    )}
                    <span className="absolute bottom-1 left-1 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-bold text-white">
                      {sim}%
                    </span>
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-display text-sm font-bold leading-tight">
                      {m.title}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {m.artist}
                      {m.year ? ` · ${m.year}` : ""}
                    </div>
                    {m.genre && (
                      <div className="mt-1 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                        {m.genre}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => doAdd(m, "owned")}
                    disabled={!!pending}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-primary px-3 py-2 text-xs font-bold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                  >
                    {addingOwned ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Disc3 className="h-3.5 w-3.5" />
                    )}
                    Adicionar
                  </button>
                  <button
                    onClick={() => doAdd(m, "wishlist")}
                    disabled={!!pending}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-border bg-surface px-3 py-2 text-xs font-semibold text-foreground hover:bg-surface/70 disabled:opacity-50"
                  >
                    {addingWish ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Heart className="h-3.5 w-3.5" />
                    )}
                    Quero esse
                  </button>
                  <button
                    onClick={() => onOpenDetail(m)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border bg-surface px-3 py-2 text-xs font-semibold text-foreground hover:bg-surface/70"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Detalhes
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-border px-4 py-3 text-center text-[11px] text-muted-foreground">
          Análise feita no seu navegador com CLIP · resultados via busca vetorial
        </div>
      </motion.div>
    </motion.div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Result modal — full album sheet
// ───────────────────────────────────────────────────────────────────────────

function ResultModal({
  result,
  barcode,
  onClose,
  onScanAgain,
}: {
  result: GarimpoResult;
  barcode: string | null;
  onClose: () => void;
  onScanAgain: () => void;
}) {
  const { isAuthenticated } = useCurrentUser();
  const qc = useQueryClient();
  const add = useServerFn(addAlbum);
  const listAlbumsFn = useServerFn(listMyAlbums);

  const ownedQ = useQuery({
    queryKey: ["my-albums", "all"],
    queryFn: () => listAlbumsFn({ data: {} }),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  const ownedSame = ownedQ.data?.find(
    (a) => String(a.discogs_id) === String(result.discogsId),
  );
  const ownedOtherSameArtist =
    !ownedSame &&
    ownedQ.data?.find(
      (a) =>
        a.artist?.toLowerCase().trim() === result.artist.toLowerCase().trim() &&
        a.title?.toLowerCase().includes(result.title.toLowerCase().split(" ")[0]),
    );

  const priceBrl =
    result.lowestPriceUsd != null ? Math.round(result.lowestPriceUsd * 5.2) : null;
  const aboveAverage =
    priceBrl != null && result.demandScore != null && result.demandScore > 60;

  const addMut = useMutation({
    mutationFn: (status: "owned" | "wishlist") =>
      add({
        data: {
          discogsId: result.discogsId,
          status,
          listing: "none",
          title: result.title,
          artist: result.artist,
          year: result.year,
          cover: result.cover,
          genres: result.genres,
        },
      }),
    onSuccess: (_d, status) => {
      toast.success(
        status === "owned" ? "Adicionado à coleção 🎉" : "Salvo na wishlist",
      );
      qc.invalidateQueries({ queryKey: ["my-collection"] });
      qc.invalidateQueries({ queryKey: ["my-wishlist"] });
      qc.invalidateQueries({ queryKey: ["my-albums"] });
    },
    onError: (e: Error) => {
      toast.error(
        e.message.includes("Unauthorized")
          ? "Faça login para salvar discos"
          : e.message,
      );
    },
  });

  const share = async () => {
    const url = `https://viny.club/album/${result.discogsId}`;
    const text = `${result.artist} — ${result.title}`;
    try {
      if (navigator.share) await navigator.share({ title: text, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado");
      }
    } catch {}
  };

  const spotifyUrl = `https://open.spotify.com/search/${encodeURIComponent(
    `${result.artist} ${result.title}`,
  )}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] overflow-y-auto bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", stiffness: 320, damping: 32 }}
        onClick={(e) => e.stopPropagation()}
        className="mx-auto my-0 min-h-screen w-full max-w-2xl bg-background sm:my-8 sm:min-h-0 sm:rounded-3xl sm:border sm:border-border sm:shadow-2xl"
      >
        {/* header image */}
        <div className="relative">
          <div className="aspect-[4/3] w-full overflow-hidden sm:rounded-t-3xl">
            {result.cover ? (
              <img loading="lazy" decoding="async"
                src={result.cover}
                alt={result.title}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="h-full w-full bg-surface" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
          </div>
          <button
            onClick={onClose}
            className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-full bg-black/60 text-white backdrop-blur"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="absolute inset-x-4 bottom-4 sm:inset-x-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
              Boa descoberta
            </div>
            <h2 className="mt-1 font-display text-2xl font-bold leading-tight sm:text-3xl">
              {result.title}
            </h2>
            <div className="mt-1 text-sm text-muted-foreground">
              {result.artist}
              {result.year ? ` · ${result.year}` : ""}
              {result.country ? ` · ${result.country}` : ""}
            </div>
          </div>
        </div>

        <div className="space-y-6 px-4 pb-10 pt-4 sm:px-6">
          {/* alerts */}
          {ownedSame && (
            <Alert tone="success" icon={CheckCircle2}>
              Já está na sua coleção
            </Alert>
          )}
          {!ownedSame && ownedOtherSameArtist && (
            <Alert tone="info" icon={Sparkles}>
              Você já possui outra edição: {ownedOtherSameArtist.title}
            </Alert>
          )}
          {aboveAverage && (
            <Alert tone="warning" icon={TrendingUp}>
              Esse disco está valorizado — acima do preço médio do mercado
            </Alert>
          )}

          {/* market section */}
          <Section title="Mercado">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                label="Preço médio"
                value={
                  priceBrl != null ? `R$ ${priceBrl}` : "—"
                }
                sub={result.lowestPriceUsd != null ? `US$ ${result.lowestPriceUsd.toFixed(2)}` : undefined}
              />
              <Stat
                label="À venda"
                value={result.numForSale != null ? String(result.numForSale) : "—"}
              />
              <Stat
                label="Raridade"
                value={
                  result.haveCount != null
                    ? result.haveCount < 200
                      ? "Rara"
                      : result.haveCount < 1500
                        ? "Incomum"
                        : "Comum"
                    : "—"
                }
                sub={result.haveCount != null ? `${result.haveCount} têm` : undefined}
              />
              <Stat
                label="Demanda"
                value={
                  result.demandScore != null ? `${result.demandScore}/100` : "—"
                }
                sub={result.wantCount != null ? `${result.wantCount} querem` : undefined}
              />
            </div>
          </Section>

          {/* quality */}
          <Section title="Prensagem & comunidade">
            <div className="grid gap-3 sm:grid-cols-2">
              <InfoLine label="Formato" value={result.format || "—"} />
              <InfoLine label="Selo" value={result.label || "—"} />
              <InfoLine label="País" value={result.country || "—"} />
              <InfoLine
                label="Avaliação"
                value={
                  result.rating != null
                    ? `${result.rating.toFixed(1)} ★ (${result.ratingCount ?? 0})`
                    : "—"
                }
              />
              {result.otherVersions != null && result.otherVersions > 1 && (
                <InfoLine
                  label="Outras prensagens"
                  value={`${result.otherVersions} versões`}
                />
              )}
            </div>
            {result.notes && (
              <p className="mt-3 rounded-xl border border-border bg-surface/50 p-3 text-xs leading-relaxed text-muted-foreground">
                {result.notes}
              </p>
            )}
          </Section>

          {/* actions */}
          <Section title="No Viny">
            <div className="grid gap-2 sm:grid-cols-2">
              <ActionButton
                primary
                disabled={!!ownedSame || addMut.isPending}
                onClick={() => addMut.mutate("owned")}
                icon={Disc3}
              >
                {ownedSame
                  ? "Já está na coleção"
                  : addMut.isPending
                    ? "Salvando..."
                    : "Adicionar à coleção"}
              </ActionButton>
              <ActionButton
                onClick={() => addMut.mutate("wishlist")}
                disabled={addMut.isPending}
                icon={Heart}
              >
                Salvar na wishlist
              </ActionButton>
              <ActionButton onClick={share} icon={Share2}>
                Compartilhar
              </ActionButton>
              <a
                href={spotifyUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 rounded-full border border-border bg-surface px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-surface/70"
              >
                <ExternalLink className="h-4 w-4" />
                Ouvir no Spotify
              </a>
            </div>
            <Link
              to="/album/$id"
              params={{ id: buildAlbumParam(result.discogsId, result.artist, result.title) }}
              className="mt-3 block text-center text-xs font-semibold text-muted-foreground underline-offset-4 hover:underline"
            >
              Ver página completa do álbum →
            </Link>
          </Section>

          <div className="flex items-center justify-between gap-3 border-t border-border pt-4 text-xs text-muted-foreground">
            <span>{barcode ? `Código ${barcode}` : "Busca manual"}</span>
            <button
              onClick={onScanAgain}
              className="inline-flex items-center gap-1.5 font-semibold text-foreground hover:text-primary"
            >
              <Camera className="h-3.5 w-3.5" /> Escanear outro
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface/40 p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1.5 font-display text-lg font-bold leading-none">
        {value}
      </div>
      {sub && <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function InfoLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate text-right font-medium">{value}</span>
    </div>
  );
}

function Alert({
  tone,
  icon: Icon,
  children,
}: {
  tone: "success" | "warning" | "info";
  icon: typeof CheckCircle2;
  children: React.ReactNode;
}) {
  const styles = {
    success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    warning: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    info: "border-primary/30 bg-primary/10 text-primary",
  }[tone];
  return (
    <div
      className={`flex items-start gap-2.5 rounded-2xl border px-3.5 py-2.5 text-sm font-medium ${styles}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function ActionButton({
  children,
  icon: Icon,
  onClick,
  disabled,
  primary,
}: {
  children: React.ReactNode;
  icon: typeof Disc3;
  onClick?: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        primary
          ? "bg-primary text-primary-foreground hover:opacity-90"
          : "border border-border bg-surface text-foreground hover:bg-surface/70"
      }`}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Manual search modal — fallback when scan fails
// ───────────────────────────────────────────────────────────────────────────

function ManualSearchModal({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (releaseId: string) => void;
}) {
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");
  const lookup = useServerFn(lookupByQuery);
  type Item = { id: string; title: string; cover: string; year: number | null };

  const { data, isFetching } = useQuery({
    queryKey: ["garimpo-query", submitted],
    queryFn: () => lookup({ data: { query: submitted } }),
    enabled: submitted.length > 1,
    staleTime: 60_000,
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="mx-auto my-0 flex min-h-screen w-full max-w-2xl flex-col bg-background sm:my-12 sm:min-h-0 sm:max-h-[80vh] sm:rounded-3xl sm:border sm:border-border sm:shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-6">
          <h3 className="font-display text-lg font-bold">Buscar disco</h3>
          <button
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full hover:bg-surface"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(q.trim());
          }}
          className="relative px-4 pt-4 sm:px-6"
        >
          <Search className="pointer-events-none absolute left-7 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground sm:left-9" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Artista, álbum, código de barras..."
            className="h-11 w-full rounded-full border border-input bg-surface pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
          />
        </form>

        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {isFetching && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Buscando no acervo...
            </div>
          )}
          {!isFetching && submitted && (data?.items?.length ?? 0) === 0 && (
            <p className="py-12 text-center text-sm text-muted-foreground">
              Nada encontrado para "{submitted}".
            </p>
          )}
          {!submitted && (
            <p className="py-12 text-center text-sm text-muted-foreground">
              Digite o nome do disco ou artista para começar o garimpo.
            </p>
          )}
          <div className="grid gap-2">
            {data?.items?.map((it: Item) => (
              <button
                key={it.id}
                onClick={() => onPick(it.id)}
                className="flex items-center gap-3 rounded-2xl border border-border bg-surface p-3 text-left transition-colors hover:border-primary/40 hover:bg-surface/70"
              >
                <img loading="lazy" decoding="async"
                  src={it.cover || ALBUM_COVER_FALLBACK}
                  onError={handleCoverError}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{it.title}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {it.year ? `${it.year} · ` : ""}Discogs #{it.id}
                  </div>
                </div>
                <Sparkles className="h-4 w-4 text-primary" />
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
